// The Mode menu (security review: role switching only for Google accounts).
// A Google sign-up (authProvider google_demo) with a Non-Penyedia role picks
// which of the four Non-Penyedia roles it works as. That role carries no
// approval authority (the API scopes such an account to its own documents),
// so switching is free. Shown in the ChatShell sidebar foot, between the
// Mode gelap row and the profile row: a full-width row in the sidebar and
// the drawer, one 44px icon button in the collapsed rail. Both open the same
// ui/Menu with one menuitemradio per role, the current one checked.
//
// Selecting a role calls PATCH /me/role, then GET /auth/me; the new role
// re-keys GET /access/features (shell/features.tsx), so the rows follow. A
// 403 role_change_forbidden (a password account, or a role outside the
// four) shows the API's message in a dialog; closing it re-reads the
// session, which hides the menu when the account may not switch. Password (admin-made)
// Non-Penyedia accounts, the admin and guests never see it.
//
// A Penyedia (any account) gets the two modes of Simulasi Pluto v4 instead:
// Penyedia Jasa Badan Usaha and Penyedia Jasa Perorangan (PATCH
// /me/penyedia-type). The menus and features stay the same; the mode decides
// the name a Penyedia signs documents with (the business, or the person)
// and what Pluto knows of the business form.
import { useCallback, useState } from "react";
import * as API from "../api";
import type { User } from "../api";
import { useAuth } from "../auth";
import { isGoogleAccount } from "../lib/account";
import { isNonPenyedia, NON_PENYEDIA_ROLES, ROLE_LABEL } from "../lib/labels";
import { Dialog } from "../ui/components";
import { Menu } from "../ui/Menu";
import { Glyph } from "../ui/icons";

/** True when `user` may switch roles (Non-Penyedia) or modes (Penyedia) from the Mode menu. */
export function canSwitchRole(user: User | null): boolean {
  if (user?.role === "penyedia") return true;
  return isGoogleAccount(user) && isNonPenyedia(user!.role);
}

/** The two Penyedia modes of Simulasi Pluto v4. */
export const PENYEDIA_MODE_LABEL: Record<API.PenyediaType, string> = {
  badan_usaha: "Penyedia Jasa Badan Usaha",
  perorangan: "Penyedia Jasa Perorangan",
};

export const PENYEDIA_MODES = Object.keys(PENYEDIA_MODE_LABEL) as API.PenyediaType[];

export type RoleModeVariant = "row" | "icon" | "field";

export function RoleModeMenu({ user, variant, labelledBy }: { user: User; variant: RoleModeVariant; labelledBy?: string }) {
  const { refresh, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{ message: string; recheck: boolean } | null>(null);
  const [announce, setAnnounce] = useState("");

  const choose = useCallback(
    async (role: (typeof NON_PENYEDIA_ROLES)[number]) => {
      if (busy || role === user.role) return;
      setBusy(true);
      setAnnounce("");
      try {
        await API.switchRole(role);
        const fresh = await refresh();
        setAnnounce(`Mode diganti ke ${ROLE_LABEL[fresh?.role ?? role]}.`);
      } catch (err) {
        const forbidden = err instanceof API.ApiError && err.status === 403;
        // The account may have changed (made a password account, a Penyedia):
        // once the dialog closes the session decides whether the menu stays.
        setFailure({ message: forbidden ? err.message || "Peran akun ini diatur oleh administrator." : API.errorMessage(err), recheck: forbidden });
      } finally {
        setBusy(false);
      }
    },
    [busy, user.role],
  );

  const choosePenyedia = useCallback(
    async (mode: API.PenyediaType) => {
      if (busy || mode === user.penyediaType) return;
      setBusy(true);
      setAnnounce("");
      try {
        const fresh = await API.setPenyediaType(mode);
        setUser(fresh);
        setAnnounce(`Mode diganti ke ${PENYEDIA_MODE_LABEL[mode]}.`);
      } catch (err) {
        setFailure({ message: API.errorMessage(err), recheck: false });
      } finally {
        setBusy(false);
      }
    },
    [busy, user.penyediaType],
  );

  const closeFailure = () => {
    const recheck = failure?.recheck === true;
    setFailure(null);
    if (recheck) void refresh();
  };

  const penyedia = user.role === "penyedia";
  const current = penyedia ? (user.penyediaType ? PENYEDIA_MODE_LABEL[user.penyediaType] : "Pilih mode Penyedia") : ROLE_LABEL[user.role];
  const items = penyedia
    ? PENYEDIA_MODES.map((mode) => ({
        id: mode,
        label: PENYEDIA_MODE_LABEL[mode],
        checked: mode === user.penyediaType,
        disabled: busy,
        onSelect: () => void choosePenyedia(mode),
      }))
    : NON_PENYEDIA_ROLES.map((role) => ({
        id: role,
        label: ROLE_LABEL[role],
        checked: role === user.role,
        disabled: busy,
        onSelect: () => void choose(role),
      }));

  const name = `Mode peran, ${current}`;
  const common = { "aria-busy": busy || undefined } as const;
  let trigger: Parameters<typeof Menu>[0]["trigger"];
  if (variant === "icon") {
    trigger = ({ ref, props }) => (
      <button ref={ref} type="button" className="ui-icon-btn cs-role-trigger" aria-label={name} title={`Mode: ${current}`} {...common} {...props}>
        <Glyph name="profile2user" />
      </button>
    );
  } else if (variant === "row") {
    trigger = ({ ref, props }) => (
      <button ref={ref} type="button" className="cs-item cs-role-trigger cs-role-row" aria-label={name} title={`Mode: ${current}`} {...common} {...props}>
        <span className="cs-item-icon">
          <Glyph name="profile2user" size={18} />
        </span>
        <span className="cs-role-text" aria-hidden="true">
          <span className="cs-role-name">Mode</span>
          <span className="cs-role-value">{current}</span>
        </span>
        <span className="cs-role-chevron" aria-hidden="true">
          <Glyph name="chevronRight" size={16} />
        </span>
      </button>
    );
  } else {
    // A select-like control: the value, and the chevron in its own lane.
    trigger = ({ ref, props }) => (
      <button ref={ref} type="button" className="role-field" aria-label={name} aria-describedby={labelledBy} {...common} {...props}>
        <span className="role-field-value">{current}</span>
        <span className="role-field-chevron" aria-hidden="true">
          <Glyph name="chevronRight" size={16} />
        </span>
      </button>
    );
  }

  return (
    <>
      <div className={variant === "field" ? "role-field-wrap" : "cs-role"}>
        <Menu label="Mode peran" items={items} align="start" placement={variant === "field" ? "below" : "above"} className="cs-role-menu" trigger={trigger} />
      </div>
      <p className="visually-hidden role-live" role="status" aria-live="polite">
        {announce}
      </p>
      <Dialog
        open={failure !== null}
        size="sm"
        title="Mode belum diganti"
        onClose={closeFailure}
        footer={
          <button type="button" className="btn" onClick={closeFailure}>
            Tutup
          </button>
        }
      >
        <p>{failure?.message}</p>
      </Dialog>
    </>
  );
}
